import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdSlot } from "@/components/AdSlot";
import api from "@/lib/api";
import { LANDING_ICONS, resolveLandingImg } from "@/lib/landingIcons";
import { ShieldCheck, Star, Stethoscope, ArrowRight, Quote } from "lucide-react";

const DEFAULT_HERO_IMG =
  "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3IlMjBmcm9udHxlbnwwfHx8fDE3ODI1MjEwNTR8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [c, setC] = useState(null);
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "1";

  useEffect(() => {
    const url = isPreview ? "/admin/content/landing/preview" : "/content/landing";
    api.get(url)
      .then(({ data }) => setC(data))
      .catch(() => {
        if (isPreview) api.get("/content/landing").then(({ data }) => setC(data)).catch(() => setC(null));
        else setC(null);
      });
  }, [isPreview]);

  if (!c) {
    return <div data-testid="landing-page" className="container-x py-24 text-[#5C6B64]">Loading…</div>;
  }

  const hero = c.hero || {};
  const steps = c.steps || [];
  const testimonials = c.testimonials || [];
  const heroImg = resolveLandingImg(hero.image_url, DEFAULT_HERO_IMG);

  return (
    <div data-testid="landing-page">
      {isPreview && (
        <div data-testid="landing-preview-banner" className="bg-[#CC5A3A] text-white text-center text-sm py-2 px-4 font-medium">
          Preview mode — showing your unpublished draft. Publish from the Marketing tab to make it live.
        </div>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="container-x grid md:grid-cols-12 gap-10 lg:gap-16 pt-16 lg:pt-28 pb-24 lg:pb-32 items-center">
          <div className="md:col-span-7">
            {hero.eyebrow && (
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-6" data-testid="hero-eyebrow">
                {hero.eyebrow}
              </div>
            )}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E3F33] leading-[1.05] tracking-tight" data-testid="hero-heading">
              {hero.heading}
            </h1>
            <p className="mt-6 text-lg text-[#5C6B64] max-w-xl leading-relaxed" data-testid="hero-subtext">
              {hero.subtext}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {hero.primary_cta_label && (
                <Link to={hero.primary_cta_link || "/search"} data-testid="hero-find-cta" className="btn-primary inline-flex items-center gap-2">
                  {hero.primary_cta_label} <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
                </Link>
              )}
              {hero.secondary_cta_label && (
                <Link to={hero.secondary_cta_link || "/register"} data-testid="hero-collector-cta" className="btn-secondary">
                  {hero.secondary_cta_label}
                </Link>
              )}
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#5C6B64]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D6A4F]" strokeWidth={1.6} /> Certified credentials</div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-[#CC5A3A]" strokeWidth={1.6} /> Real reviews</div>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={heroImg}
                alt="Certified specimen collector"
                className="rounded-3xl w-full h-[520px] object-cover shadow-md"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad bg-[#F0EFEA]/60" data-testid="landing-how">
        <div className="container-x">
          <div className="max-w-2xl">
            {c.how_eyebrow && <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-4">{c.how_eyebrow}</div>}
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1E3F33]">{c.how_title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {steps.map((s, i) => {
              const Icon = LANDING_ICONS[s.icon] || LANDING_ICONS.MapPin;
              return (
                <div key={i} className="card-soft" data-testid={`how-step-${i}`}>
                  <Icon className="w-7 h-7 text-[#CC5A3A]" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl mt-4 text-[#1E3F33] font-medium">{s.title}</h3>
                  <p className="text-[#5C6B64] mt-2">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOR COLLECTORS */}
      <section className="section-pad" data-testid="landing-collectors">
        <div className="container-x grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <img
              src="https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxoZWFsdGglMjBibG9vZCUyMHRlc3QlMjBraXQlMjBsYWJ8ZW58MHx8fHwxNzgyNDM1MzY5fDA&ixlib=rb-4.1.0&q=85"
              alt="Blood test kit"
              className="rounded-3xl w-full h-[440px] object-cover shadow-md"
            />
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-4">For collectors</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1E3F33]">
              Grow your collection business with a steady flow of local customers.
            </h2>
            <ul className="mt-6 space-y-3 text-[#5C6B64]">
              <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-[#2D6A4F] mt-0.5" strokeWidth={1.6}/> Upload your certifications to earn a certified badge.</li>
              <li className="flex gap-3"><Stethoscope className="w-5 h-5 text-[#2D6A4F] mt-0.5" strokeWidth={1.6}/> List your services, supplies, and service areas.</li>
              <li className="flex gap-3"><Star className="w-5 h-5 text-[#2D6A4F] mt-0.5" strokeWidth={1.6}/> Build your reputation with certified customer reviews.</li>
            </ul>
            <div className="mt-8">
              <Link to="/register" data-testid="collector-register-cta" className="btn-accent inline-flex items-center gap-2">
                Join as a collector <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
              </Link>
              <span className="ml-4 text-sm text-[#5C6B64]">$10.99/mo · cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="section-pad bg-[#F0EFEA]/60" data-testid="landing-testimonials">
          <div className="container-x">
            <div className="max-w-2xl">
              {c.testimonials_eyebrow && <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-4">{c.testimonials_eyebrow}</div>}
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1E3F33]">{c.testimonials_title}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {testimonials.map((t, i) => (
                <div key={i} className="card-soft flex flex-col" data-testid={`testimonial-${i}`}>
                  <Quote className="w-7 h-7 text-[#CC5A3A]" strokeWidth={1.5} />
                  <p className="text-[#1E3F33] mt-4 leading-relaxed flex-1">“{t.quote}”</p>
                  <div className="mt-4 flex items-center gap-2">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className={`w-4 h-4 ${s < (t.rating || 5) ? "fill-[#CC5A3A] text-[#CC5A3A]" : "text-[#D8D5CC]"}`} />
                    ))}
                  </div>
                  <div className="text-sm text-[#5C6B64] mt-2 font-medium">{t.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="container-x pb-4" data-testid="landing-ads">
        <AdSlot placement="landing" className="max-w-2xl mx-auto" />
      </section>
    </div>
  );
}
